import { useCallback, useEffect, useRef, useState } from 'react'
import { useClient } from 'urql'
import type { WorkflowTriggerInput } from 'app/generated/graphql'
import { DAY_MS, PAGE_SIZE, loadLookback, type LookbackLoad } from 'app/lib/workflows/lookback/loadLookback'
import type { MatchWindowDays, PreviewWindow } from 'app/lib/workflows/lookback/types'
import { logError } from 'lib/logging'

export type TriggerLookback = LookbackLoad & {
  status: `idle` | `loading` | `loadingMore` | `ready` | `error`
  error?: string
  loadedAt?: Date
  refresh: () => void
  /** Whether the engine holds matches past the pages loaded. */
  hasMore: boolean
  /** Appends the next page of older matches. */
  loadMore: () => void
}

type State = Omit<TriggerLookback, `refresh` | `loadMore`>

const EMPTY: LookbackLoad = { rows: [], considered: 0, rejections: [], total: 0, eventHistoryStart: null }
// Typing a condition value changes the question on every keystroke; ask once it settles.
const SETTLE_MS = 300

/** Ahead: who a date trigger reaches next. Since: the past events going live would replay. */
export type LookbackRange = { aheadDays: MatchWindowDays } | { since: string }

/** The single seam between the trigger preview and the panels that show it. */
export function useTriggerLookback({
  open,
  trigger,
  locationId,
  range,
  workflowId,
  targetPipelineId,
}: {
  open: boolean
  /** The trigger as compiled for the engine, straight off the canvas. */
  trigger?: WorkflowTriggerInput
  locationId?: string
  range: LookbackRange
  workflowId?: string
  targetPipelineId?: string
}): TriggerLookback {
  const client = useClient()
  const [state, setState] = useState<State>({ status: `idle`, hasMore: false, ...EMPTY })
  const [tick, setTick] = useState(0)
  const latest = useRef(0)
  // Later pages are asked for against the same window, so the offsets can't shift under them.
  const previewWindow = useRef<PreviewWindow>({ start: new Date(), end: new Date() })
  const pages = useRef(1)
  const lastQuery = useRef(``)
  // The builder recompiles the trigger into a fresh object every render; key the effect on its content.
  const triggerKey = JSON.stringify(trigger ?? null)
  const rangeKey = JSON.stringify(range)
  // Upcoming dates read soonest first; past events newest first.
  const order = `aheadDays` in range ? `soonest` : `newest`

  useEffect(() => {
    const current: WorkflowTriggerInput | null = JSON.parse(triggerKey)
    if (!open || !current || !locationId) return
    // A new question starts at one page; a refresh of the same question reloads every page shown.
    const queryKey = JSON.stringify([triggerKey, locationId, rangeKey, workflowId, targetPipelineId])
    const fresh = queryKey !== lastQuery.current
    if (fresh) pages.current = 1
    lastQuery.current = queryKey
    const request = ++latest.current
    const count = pages.current
    // A refresh keeps the last good rows on screen; a new question never shows the old answer as its own.
    setState((previous) =>
      fresh ? { status: `loading`, hasMore: false, ...EMPTY } : { ...previous, status: `loading`, error: undefined },
    )
    const timer = setTimeout(() => {
      const now = new Date()
      const asked: LookbackRange = JSON.parse(rangeKey)
      previewWindow.current =
        `aheadDays` in asked
          ? { start: now, end: new Date(now.getTime() + asked.aheadDays * DAY_MS) }
          : { start: new Date(asked.since), end: now }
      loadLookback(
        client,
        current,
        locationId,
        previewWindow.current,
        workflowId,
        targetPipelineId,
        { from: 0, count },
        order,
      ).then(
        (loaded) => {
          // A slower, older request must never overwrite a newer answer.
          if (request !== latest.current) return
          setState({ status: `ready`, ...loaded, hasMore: count * PAGE_SIZE < loaded.total, loadedAt: new Date() })
        },
        (error: unknown) => {
          logError(`Trigger lookback failed`, error)
          if (request !== latest.current) return
          // Last good rows stay; the panel puts the error above them.
          setState((previous) => ({
            ...previous,
            status: `error`,
            error: error instanceof Error ? error.message : String(error),
          }))
        },
      )
    }, SETTLE_MS)
    return () => clearTimeout(timer)
  }, [open, triggerKey, locationId, rangeKey, order, workflowId, targetPipelineId, tick, client])

  const loadMore = useCallback(() => {
    const current: WorkflowTriggerInput | null = JSON.parse(triggerKey)
    if (!current || !locationId) return
    const request = latest.current
    const from = pages.current
    setState((previous) => ({ ...previous, status: `loadingMore`, error: undefined }))
    loadLookback(
      client,
      current,
      locationId,
      previewWindow.current,
      workflowId,
      targetPipelineId,
      { from, count: 1 },
      order,
    ).then(
      (loaded) => {
        // A refresh or a new question since the click owns the list now.
        if (request !== latest.current) return
        pages.current = from + 1
        setState((previous) => {
          const seen = new Set(previous.rows.map((row) => row.match.id))
          return {
            ...previous,
            ...loaded,
            rows: [...previous.rows, ...loaded.rows.filter((row) => !seen.has(row.match.id))],
            hasMore: (from + 1) * PAGE_SIZE < loaded.total,
            status: `ready`,
          }
        })
      },
      (error: unknown) => {
        logError(`Trigger lookback load more failed`, error)
        if (request !== latest.current) return
        setState((previous) => ({
          ...previous,
          status: `error`,
          error: error instanceof Error ? error.message : String(error),
        }))
      },
    )
  }, [triggerKey, locationId, order, workflowId, targetPipelineId, client])

  const refresh = useCallback(() => setTick((value) => value + 1), [])
  return { ...state, refresh, loadMore }
}
